import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tooltip, message } from 'antd';
import {
    ArrowLeftOutlined,
    ExportOutlined,
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import { Sparkles } from 'lucide-react';
import { useEditorContext } from '../contexts/EditorContext';
import { SaveStatus } from './SaveStatus';
import { exportWord } from '../utils/exportWord';
import { PANEL_TAB } from './constants';
import { findNearestRewritableHeading } from '../utils/selectionUtils';
import styles from '../styles/index.module.scss';

/**
 * 编辑器顶栏：返回订单 / 标题 / 保存状态 / 导出 Word / 改稿
 */
export const EditorTopBar = ({ isMobile, isCompact }) => {
    const navigate = useNavigate();
    const { editor, uiState, documentState, openToolPanel, selectionState } = useEditorContext();
    const [exporting, setExporting] = useState(false);

    const orderNo = documentState.orderNo;

    const handleBack = () => {
        if (orderNo) {
            navigate(`/order/result?no=${encodeURIComponent(orderNo)}`);
        } else {
            navigate('/order/result');
        }
    };

    const handleExport = async () => {
        if (!orderNo) {
            message.warning('订单号不存在，无法导出');
            return;
        }
        try {
            setExporting(true);
            await exportWord(orderNo);
            message.success('导出成功');
        } catch (error) {
            console.error('导出失败:', error);
            message.error(error?.message || '导出失败，请稍后重试');
        } finally {
            setExporting(false);
        }
    };

    /** 顶栏「改稿」：打开章节改稿页签；没选章节时默认选中光标附近的章节 */
    const handleRevise = () => {
        if (editor && !selectionState.isHeadingSelected) {
            const nearest = findNearestRewritableHeading(editor);
            if (nearest) {
                editor.commands.setNodeSelection(nearest.pos);
            }
        }
        openToolPanel(PANEL_TAB.CHAPTER);
    };

    const toggleOutline = () => {
        if (isCompact) {
            uiState.outlineOpen = !uiState.outlineOpen;
        } else {
            uiState.outlineCollapsed = !uiState.outlineCollapsed;
        }
    };

    const outlineIcon = isCompact ? (
        <UnorderedListOutlined />
    ) : uiState.outlineCollapsed ? (
        <MenuUnfoldOutlined />
    ) : (
        <MenuFoldOutlined />
    );

    return (
        <header className={styles.topBar}>
            <div className={styles.topBarLeft}>
                <Tooltip title="返回订单详情" placement="bottom">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleBack}
                        className={styles.topBarBack}
                    >
                        {!isMobile && '返回订单'}
                    </Button>
                </Tooltip>
                <Tooltip title={isCompact ? '打开大纲' : '收起 / 展开大纲'} placement="bottom">
                    <Button
                        type="text"
                        icon={outlineIcon}
                        onClick={toggleOutline}
                        className={styles.topBarIconBtn}
                        aria-label="大纲"
                    />
                </Tooltip>
                <div className={styles.topBarDivider} />
                <div className={styles.topBarTitleWrap}>
                    <div className={styles.topBarTitle} title={uiState.paperTitle}>
                        {uiState.paperTitle || (uiState.loading ? '正在载入论文…' : '未命名论文')}
                    </div>
                    {!isMobile && orderNo && (
                        <div className={styles.topBarMeta}>订单号 {orderNo}</div>
                    )}
                </div>
            </div>

            <div className={styles.topBarRight}>
                <SaveStatus compact={isMobile} />
                <Button
                    icon={<ExportOutlined />}
                    onClick={handleExport}
                    loading={exporting}
                    disabled={uiState.loading || !!uiState.loadError}
                    className={styles.topBarExport}
                >
                    {!isMobile && '导出 Word'}
                </Button>
                <Button
                    type="primary"
                    icon={<Sparkles size={15} />}
                    onClick={handleRevise}
                    disabled={uiState.loading || !!uiState.loadError}
                    className={styles.topBarRevise}
                >
                    改稿
                </Button>
            </div>
        </header>
    );
};

export default EditorTopBar;
